import _ from "lodash";
import React, { createRef } from "react";
import classes from "./TeacherLogin.scss";
import { GOOGLE_CLIENT_ID, LEX, LEX_PILOT } from "Constants";
import GoogleLoginBtn from "UIComponents/GoogleLoginBtn";
import MicrosoftLoginBtn from "UIComponents/MicrosoftLoginBtn";
import CustomLoginButton from "UIComponents/CustomLoginButton";
import GridLayoutWrapper from "UIComponents/GridLayoutWrapper";
import {FormTextInput} from "UIComponents/InputTextField";
import {
  CleverColored,
  ProfileOutlined,
  MailColored,
  LexColored,
  SchoolOutlined,
} from "@toddle-edu/ds-icons";
import { Button, DropdownMenu } from "@toddle-edu/ds-web";
import ThirdPartyAuth from "AppComponents/ThirdPartyAuth";
import classNames from "classnames";

const styles = {
  innerConStyle: {
    width: "auto",
  },
  containerStyle: {
    height: "48px",
    marginBottom: "0px",
  },
};

class TeacherLogin extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      showError: false,
    };

    this.inputRef = createRef(null);
  }

  isValid = value => {
    return _.trim(value).length > 0;
  };

  onLoginClick = () => {
    const { updateCurrentScreen, enableLoginFlowV2 } = this.props;
    updateCurrentScreen({
      type: enableLoginFlowV2 ? "loginFormV3" : "loginForm",
    });
  };

  onCreateAccountClick = () => {
    const { updateCurrentScreen } = this.props;
    updateCurrentScreen({ type: "organizationSelection" });
  };

  onBackClick = () => {
    const {
      updateCurrentScreen,
      resetScreen,
      isNewLoginFlowEnabled,
    } = this.props;
    if (isNewLoginFlowEnabled) {
      resetScreen("teacherLogin");
    }
    updateCurrentScreen({ type: "loginHome", paramsToBeRemoved: ["usertype"] });
  };

  updateCleverAuthRef = ref => {
    this.authCleverRef = _.get(ref, "refs.wrappedInstance", {});
  };

  updateLexAuthRef = ref => {
    this.authLexRef = _.get(ref, "refs.wrappedInstance", {});
  };

  updateLexPilotAuthRef = ref => {
    this.authLexPilotRef = _.get(ref, "refs.wrappedInstance", {});
  };

  onEmailSubmit = async () => {
    const {
      updateCurrentScreen,
      userType,
      generateEmailOTPForSignIn,
      isOTPLoginEnabled,
      updateInputField,
      data: { email = "" } = {},
    } = this.props;

    const errorCount = this.inputRef.isValid();
    if (errorCount > 0) {
      return;
    }

    if (isOTPLoginEnabled) {
      const { isSuccess } = await generateEmailOTPForSignIn({ userType });
      if (isSuccess) {
        updateCurrentScreen({ type: "loginFormV2" });
      }
    } else {
      updateInputField({ email }, "loginForm");
      updateCurrentScreen({ type: "loginForm" });
    }
  };

  onCleverLogInClick = () => {
    const { updateToddleInitiatedValue } = this.props;
    if (this.authCleverRef) {
      updateToddleInitiatedValue(true);
      this.authCleverRef.onAuthClick();
    }
  };

  onLexLoginClick = () => {
    const { updateToddleInitiatedValue } = this.props;
    if (this.authLexRef) {
      updateToddleInitiatedValue(true);
      this.authLexRef.onAuthClick();
    }
  };

  onLexPilotLoginClick = () => {
    const { updateToddleInitiatedValue } = this.props;
    if (this.authLexPilotRef) {
      updateToddleInitiatedValue(true);
      this.authLexPilotRef.onAuthClick();
    }
  };

  getLexOptions = () => {
    return [
      {
        key: LEX_PILOT,
        label: "Lex (Global)",
        icon: <LexColored size="xx-small" />,
      },
      {
        key: LEX,
        label: "Lex (Brazil)",
        icon: <LexColored size="xx-small" />,
      },
    ];
  };

  onClickLexOption = ({ key }) => {
    if (key === LEX_PILOT) {
      this.onLexPilotLoginClick();
    } else if (key === LEX) {
      this.onLexLoginClick();
    }
  };

  onYourSchoolClick = () => {
    const { updateCurrentScreen } = this.props;
    updateCurrentScreen({ type: "schoolCodeForm" });
  };

  render() {
    const {
      onGoogleSignInCallBack,
      onGoogelFailureCallBack,
      onMicroSoftSignInCallBack,
      onMicroSoftFailureCallBack,
      onCleverSignInCallBack,
      onLexSignInCallBack,
      onLexPilotSignInCallBack,
      renderTradeMarkText,
      customStyle: { headerStyleV2 } = {},
      renderBackButtonComponent,
      isCleverEnabled,
      isLexEnabled,
      isGoogleEnabled,
      isMicrosoftEnabled,
      isCreateAccountEnabled,
      data: { email = "" } = {},
      checkEmailVerification,
      updateInputField,
      isNewLoginFlowEnabled,
      t,
      renderLoginMethods,
      loginButtonsCount,
      isOTPLoginEnabled,
      isPasswordLoginEnabled,
      isOrgWorkspaceLoginPage = false,
      isOrgWorkspaceLoginEnabled,
      organizationName,
      enableLoginFlowV2,
    } = this.props;

    const showHelperText = isOrgWorkspaceLoginPage
      ? loginButtonsCount > 0
      : isNewLoginFlowEnabled;

    const isEmailLoginFlowEnabled =
      (isOTPLoginEnabled || isPasswordLoginEnabled) && isNewLoginFlowEnabled;

    const containerStyle = {
      paddingTop: isOrgWorkspaceLoginPage ? "13%" : "19.5%",
    };

    return (
      <div
        className={classes.container}
        style={containerStyle}
        role="main"
        aria-label="Teacher Login Page"
      >
        {/* Hide the back button UI as of now */}
        <div>
          {renderBackButtonComponent({
            onClick: this.onBackClick,
            buttonText: isOrgWorkspaceLoginPage
              ? organizationName
              : "Toddle home",
            ariaLabel: "Go back to usertype selection screen",
          })}
          <div style={headerStyleV2}>
            {isOrgWorkspaceLoginPage
              ? "Sign in to your teacher account"
              : "Teacher account"}
          </div>
          <div
            className={classNames({
              [classes.buttonsContainer]: true,
              [classes.buttonsContainerV2]: isNewLoginFlowEnabled,
            })}
            role="group"
            aria-label="Login methods"
          >
            {showHelperText ? (
              <div className={classes.helperText}>{"Sign in with"}</div>
            ) : null}
            {!isOrgWorkspaceLoginPage ? (
              <GridLayoutWrapper
                noOfColumns={2}
                itemsPerRow={2}
                stretchItemsInLastRow={false}
                role="list"
                aria-label="Login options list"
              >
                {enableLoginFlowV2 && isEmailLoginFlowEnabled && (
                  <CustomLoginButton
                    key={"EMAIL_LOGIN_BTN"}
                    data-test-id={"button-login-email"}
                    icon={<MailColored size="x-small" />}
                    onClick={this.onLoginClick}
                    buttonText={"Email"}
                    innerConStyle={styles.innerConStyle}
                    containerStyle={styles.containerStyle}
                  />
                )}

                {isGoogleEnabled && (
                  <GoogleLoginBtn
                    key={"GOOGLE_LOGIN_BTN"}
                    clientId={GOOGLE_CLIENT_ID}
                    onSuccess={onGoogleSignInCallBack}
                    onFailure={onGoogelFailureCallBack}
                    prompt="select_account"
                    innerConStyle={styles.innerConStyle}
                    containerStyle={styles.containerStyle}
                  />
                )}

                {isMicrosoftEnabled && (
                  <MicrosoftLoginBtn
                    key={"MICROSOFT_LOGIN_BTN"}
                    onSuccess={onMicroSoftSignInCallBack}
                    onFailure={onMicroSoftFailureCallBack}
                    className={classes.buttonContainer}
                    innerConStyle={styles.innerConStyle}
                    containerStyle={styles.containerStyle}
                  />
                )}

                {isCleverEnabled && (
                  <CustomLoginButton
                    key={"CLEVER_LOGIN_BTN"}
                    data-test-id={"button-login-clever"}
                    icon={<CleverColored size="x-small" />}
                    onClick={this.onCleverLogInClick}
                    buttonText={"Clever"}
                    innerConStyle={styles.innerConStyle}
                    containerStyle={styles.containerStyle}
                  />
                )}

                {isLexEnabled && (
                  <CustomLoginButton
                    key={"LEX_LOGIN_BTN"}
                    data-test-id={"button-login-clever"}
                    icon={<LexColored size="x-small" />}
                    onClick={this.onLexLoginClick}
                    buttonText={"Lex"}
                    innerConStyle={styles.innerConStyle}
                    containerStyle={styles.containerStyle}
                    isSplitButton={true}
                    overlay={
                      <DropdownMenu
                        options={this.getLexOptions()}
                        onClick={this.onClickLexOption}
                      />
                    }
                  />
                )}

                {isOrgWorkspaceLoginEnabled && (
                  <CustomLoginButton
                    key={"SCHOOL_LOGIN_BTN"}
                    data-test-id={"button-login-school"}
                    icon={<SchoolOutlined size="x-small" />}
                    buttonText={"Your school"}
                    onClick={this.onYourSchoolClick}
                    innerConStyle={styles.innerConStyle}
                    containerStyle={styles.containerStyle}
                  />
                )}
              </GridLayoutWrapper>
            ) : (
              renderLoginMethods()
            )}
          </div>

          <ThirdPartyAuth
            serviceType="CLEVER"
            ref={this.updateCleverAuthRef}
            onSuccessCallBack={onCleverSignInCallBack}
          />
          <ThirdPartyAuth
            serviceType="LEX"
            ref={this.updateLexAuthRef}
            onSuccessCallBack={onLexSignInCallBack}
          />

          <ThirdPartyAuth
            serviceType="LEX_PILOT"
            ref={this.updateLexPilotAuthRef}
            onSuccessCallBack={onLexPilotSignInCallBack}
          />

          {isEmailLoginFlowEnabled ? (
            !enableLoginFlowV2 ? (
              <>
                {showHelperText ? (
                  <div
                    className={classNames({
                      [classes.orContainer]: true,
                      [classes.orContainerV2]: isNewLoginFlowEnabled,
                    })}
                  >
                    <div className={classes.orLineContainer} />
                    <div className={classes.orText}>or</div>
                    <div className={classes.orLineContainer} />
                  </div>
                ) : null}

                <div className={classes.bottomContainer}>
                  <div className={classes.emailContainer}>
                    <div className={classes.emailText}>
                      {isOTPLoginEnabled
                        ? "Sign in with email OTP"
                        : "Sign in with email"}
                    </div>
                    <div className={classes.inputRowContainer}>
                      <div className={classes.inputField}>
                        <FormTextInput
                          placeholder="Enter your email id"
                          ref={ref => (this.inputRef = ref)}
                          value={email}
                          name={"email"}
                          customValidation={checkEmailVerification}
                          size={"large"}
                          updateInputField={updateInputField}
                          onEnter={this.onEmailSubmit}
                          leadingIcon={
                            <ProfileOutlined size="xx-small" variant="subtle" />
                          }
                          error={"Enter valid email"}
                          autoComplete={"off"}
                          autoFocus={true}
                          testId={"input-login-email"}
                        />
                      </div>
                      <Button
                        variant={"destructive"}
                        disabled={email.length === 0}
                        onClick={this.onEmailSubmit}
                        size={"large"}
                        className={classes.submitButton}
                        testId="login-email-next"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                  {isCreateAccountEnabled && (
                    <div>
                      {`Don't have an account?`}
                      <span className={classes.signupButton}>
                        <Button
                          data-test-id="button-forgot-password"
                          variant={"inline-progressive"}
                          size={"small"}
                          onClick={this.onCreateAccountClick}
                        >{`Create new`}</Button>
                      </span>
                    </div>
                  )}
                </div>
              </>
            ) : null
          ) : (
            <>
              {isCreateAccountEnabled && (
                <div className={classes.orContainer}>
                  <div className={classes.orLineContainer} />
                  <div className={classes.orText}>or</div>
                  <div className={classes.orLineContainer} />
                </div>
              )}
              {isCreateAccountEnabled && (
                <CustomLoginButton
                  icon={<ProfileOutlined size="x-small" />}
                  buttonText={"Create a new account"}
                  onClick={this.onCreateAccountClick}
                  innerConStyle={styles.innerConStyle}
                />
              )}
            </>
          )}
        </div>
        <div>
          {renderTradeMarkText({
            align: "left",
          })}
        </div>
      </div>
    );
  }
}

export default TeacherLogin;
